import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button, TextInput } from '@mantine/core';
import { IconClipboardList, IconPlayerPlay, IconQrcode, IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import { useProceduresServices, type ProcedureDirectoryItem } from '@actschurch/ccms-sdk';
import { ProcedureCard } from '@/components/procedure/ProcedureCard';
import { EmptyState, ErrorState, LoadingState, SectionLabel } from '@/components/ui';
import { isNetworkError } from '@/lib/errors';
import { formatWhen } from '@/lib/format';
import { getRecentViews } from '@/lib/offlineStore';
import { usePortalMode } from '@/lib/portalApi';
import { proceduresKeys } from '@/lib/queryKeys';
import { listInProgress } from '@/lib/runStore';
import { usePortalSettings } from '@/lib/settings';

const PAGE_SIZE = 30;

/**
 * Home: find a procedure. Search, category chips and (signed in) "Shared with you". Runs left
 * unfinished on this phone come first, and with no signal the procedures opened before are listed.
 */
export default function DirectoryPage() {
  const services = useProceduresServices();
  const { mode, isInitializing, userId } = usePortalMode();
  const { settings } = usePortalSettings();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') ?? '');
  const [debounced] = useDebouncedValue(search.trim(), 300);
  const categoryParam = Number(params.get('c'));
  const categoryID = Number.isFinite(categoryParam) && categoryParam > 0 ? categoryParam : null;
  const filtered = !!debounced || categoryID !== null;

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debounced) next.set('q', debounced);
    else next.delete('q');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  const setCategory = (id: number | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set('c', String(id));
    else next.delete('c');
    setParams(next, { replace: true });
  };

  const categories = useQuery({
    queryKey: proceduresKeys.categories(mode),
    enabled: !isInitializing,
    queryFn: () => (mode === 'member' ? services.proceduresMember.getMemberCategories() : services.publicProcedures.getPublicCategories()),
    staleTime: 5 * 60 * 1000,
  });

  const directory = useInfiniteQuery({
    queryKey: proceduresKeys.directory(mode, debounced, categoryID),
    enabled: !isInitializing,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => {
      const q = { search: debounced || undefined, categoryID: categoryID ?? undefined, page: pageParam, pageSize: PAGE_SIZE };
      return mode === 'member' ? services.proceduresMember.getMemberDirectory(q) : services.publicProcedures.getPublicDirectory(q);
    },
    getNextPageParam: (last) => (last.page < last.totalPages ? last.page + 1 : undefined),
    staleTime: 60 * 1000,
  });

  const assigned = useQuery({
    queryKey: [...proceduresKeys.directory('member', '', null), 'assigned'],
    enabled: mode === 'member' && !isInitializing,
    queryFn: async () => {
      const res = await services.proceduresMember.getMemberDirectory({ page: 1, pageSize: 100 });
      return res.items.filter((i) => i.isAssigned);
    },
    staleTime: 60 * 1000,
  });

  const items: ProcedureDirectoryItem[] = useMemo(() => {
    const all = (directory.data?.pages ?? []).flatMap((p) => p.items);
    if (filtered || mode !== 'member') return all;
    const shared = new Set((assigned.data ?? []).map((i) => i.procedureID));
    return all.filter((i) => !shared.has(i.procedureID));
  }, [directory.data, assigned.data, filtered, mode]);

  const inProgress = useMemo(() => listInProgress(userId), [userId]);
  const offline = directory.isError && isNetworkError(directory.error);
  const recent = useMemo(() => (offline ? getRecentViews() : []), [offline]);
  const total = directory.data?.pages[0]?.totalCount ?? 0;
  const cats = (categories.data ?? []).filter((c) => c.isActive !== false && c.procedureCount > 0);

  return (
    <div className="proc-main">
      <header className="proc-hero">
        <h1 className="proc-title">{settings.portalName || 'Acts Procedures'}</h1>
        <p className="proc-lede">
          {settings.welcomeText || 'Standing at the equipment? Scan the QR code on it. Or find the procedure here and follow it one step at a time.'}
        </p>
      </header>

      {inProgress.length > 0 && (
        <section aria-labelledby="carry-on">
          <SectionLabel id="carry-on">Carry on where you left off</SectionLabel>
          <div className="proc-stack">
            {inProgress.map((r) => (
              <Link key={r.runId} to={`/p/${encodeURIComponent(r.code)}/run/${r.runId}`} className="proc-actioncard primary">
                <span className="glyph" aria-hidden="true"><IconPlayerPlay size={26} /></span>
                <span className="txt">
                  <span className="t">{r.title || r.code}</span>
                  <span className="d">Started {formatWhen(new Date(r.startedAtMs))}</span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="proc-search" role="search">
        <TextInput
          aria-label="Search procedures"
          placeholder="Search, for example: sound desk"
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={20} aria-hidden="true" />}
          rightSection={
            search ? (
              <button type="button" className="proc-iconbtn sm" onClick={() => setSearch('')} aria-label="Clear search">
                <IconX size={18} aria-hidden="true" />
              </button>
            ) : null
          }
          rightSectionWidth={48}
          size="lg"
          inputMode="search"
          enterKeyHint="search"
        />
      </div>

      {cats.length > 0 && (
        <div className="proc-catrow" role="group" aria-label="Categories">
          <button type="button" className="proc-chip" aria-pressed={categoryID === null} onClick={() => setCategory(null)}>All</button>
          {cats.map((c) => (
            <button key={c.categoryID} type="button" className="proc-chip" aria-pressed={categoryID === c.categoryID} onClick={() => setCategory(c.categoryID)}>
              {c.path || c.name}
            </button>
          ))}
        </div>
      )}

      {mode === 'member' && !filtered && (assigned.data?.length ?? 0) > 0 && (
        <section aria-labelledby="shared">
          <SectionLabel id="shared" hint={`${assigned.data?.length}`}>Shared with you</SectionLabel>
          <div className="proc-stack">
            {assigned.data?.map((item) => <ProcedureCard key={item.procedureID} item={item} />)}
          </div>
        </section>
      )}

      <section aria-labelledby="all">
        <SectionLabel id="all" hint={directory.isSuccess ? `${total}` : undefined}>
          {filtered ? 'Results' : mode === 'member' && (assigned.data?.length ?? 0) > 0 ? 'Everything else' : 'All procedures'}
        </SectionLabel>

        {directory.isLoading && <LoadingState label="Loading procedures" count={3} />}

        {offline && (
          <EmptyState icon={<IconQrcode size={28} />} title="No signal right now">
            {recent.length > 0
              ? 'You can still open the procedures you opened on this phone before:'
              : "Procedures you've opened before still work without signal. This list needs signal."}
          </EmptyState>
        )}
        {offline && recent.length > 0 && (
          <div className="proc-stack" style={{ marginTop: 12 }}>
            {recent.map((r) => (
              <Link key={r.code} to={`/p/${encodeURIComponent(r.code)}`} className="proc-actioncard">
                <span className="glyph" aria-hidden="true"><IconClipboardList size={26} /></span>
                <span className="txt">
                  <span className="t">{r.title}</span>
                  <span className="d">{r.locationName ? `${r.locationName} · ` : ''}Saved on this phone</span>
                </span>
              </Link>
            ))}
          </div>
        )}

        {directory.isError && !offline && (
          <ErrorState error={directory.error} onRetry={() => void directory.refetch()} retrying={directory.isFetching} />
        )}

        {directory.isSuccess && items.length === 0 && (
          <EmptyState icon={<IconSearch size={28} />} title={filtered ? 'Nothing matches that' : 'No procedures yet'}>
            {filtered ? 'Try a different word, or clear the search and the category.' : 'Procedures will show here once they are published.'}
          </EmptyState>
        )}

        {items.length > 0 && (
          <div className="proc-stack">
            {items.map((item) => <ProcedureCard key={item.procedureID} item={item} />)}
          </div>
        )}

        {directory.hasNextPage && (
          <Button variant="default" fullWidth mt="md" loading={directory.isFetchingNextPage} onClick={() => void directory.fetchNextPage()}>
            Show more
          </Button>
        )}
      </section>

      {mode === 'public' && (
        <p className="proc-footnote">
          Looking for a procedure for your team? <Link to="/auth/login">Sign in</Link> to see the ones shared with you.
        </p>
      )}
    </div>
  );
}
